// Fixed: Property manager dashboard now correctly fetches only accessible endpoints
import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Building2, LogOut, Users, TrendingUp, UserPlus, Phone, Mail, MapPin, Calendar, Star, MessageSquare, Bell, Filter, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import CompaniesManagement from "@/components/CompaniesManagement";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CRMDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newReminder, setNewReminder] = useState({ date: "", message: "" });
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssigned, setFilterAssigned] = useState("all");
  const [filterLeadType, setFilterLeadType] = useState("all"); // "all", "standard", "rent_worth"
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("leads");
  const [authChecked, setAuthChecked] = useState(false);
  const [autoApprove, setAutoApprove] = useState(true);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const dataFetchedRef = useRef(false);
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  
  // Property manager profile states
  const [companyProfile, setCompanyProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    company_name: "",
    contact_person: "",
    phone: "",
    address: "",
    website: "",
    description: "",
    management_types: []
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Admin profile states
  const [adminProfile, setAdminProfile] = useState(null);
  const [editingAdminProfile, setEditingAdminProfile] = useState(false);
  const [adminProfileData, setAdminProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    website: ""
  });
  const [adminPasswordData, setAdminPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [updatingAdminProfile, setUpdatingAdminProfile] = useState(false);
  const [changingAdminPassword, setChangingAdminPassword] = useState(false);

  useEffect(() => {
    // Only redirect if we're sure user is not authenticated (not undefined, meaning still loading)
    if (user === null && user !== undefined) {
      // User is definitely not authenticated after auth check completed
      console.log("[CRM Dashboard] User not authenticated, redirecting to home");
      navigate("/");
    } else if (user && user !== null && !dataFetchedRef.current) {
      // User is authenticated and we haven't fetched data yet
      console.log("[CRM Dashboard] User authenticated, fetching data");
      dataFetchedRef.current = true;
      fetchData();
    } else if (user === undefined) {
      // Auth is still loading, wait
      console.log("[CRM Dashboard] Auth still loading, waiting...");
    }
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      console.log("[CRM Dashboard] fetchData called, user role:", user?.role);
      
      // Base requests that all users can access
      const requests = [
        axios.get(`${API}/analytics`, { withCredentials: true }),
        axios.get(`${API}/leads`, { withCredentials: true })
      ];
      
      console.log("[CRM Dashboard] Base requests prepared (analytics, leads)");

      // Admin-only requests
      if (user?.role === "admin") {
        console.log("[CRM Dashboard] Adding admin-only requests");
        requests.push(
          axios.get(`${API}/users`, { withCredentials: true }),
          axios.get(`${API}/reminders`, { withCredentials: true }),
          axios.get(`${API}/settings`, { withCredentials: true })
        );
      } else {
        console.log("[CRM Dashboard] Skipping admin-only requests for role:", user?.role);
      }

      console.log("[CRM Dashboard] Fetching", requests.length, "requests");
      const responses = await Promise.all(requests);
      console.log("[CRM Dashboard] All requests completed successfully");

      // Set common data
      setAnalytics(responses[0].data);
      setLeads(responses[1].data);
      console.log("[CRM Dashboard] Leads set:", responses[1].data?.length, "leads");
      
      // Set admin-specific data
      if (user?.role === "admin") {
        setUsers(responses[2].data);
        setReminders(responses[3].data.filter(r => !r.completed));
        setAutoApprove(responses[4].data.auto_approve_companies ?? true);
        // Fetch admin profile
        fetchAdminProfile();
      }
      
      // Fetch company profile if property manager
      if (user?.role === "property_manager") {
        console.log("[CRM Dashboard] Fetching company profile for property manager");
        fetchCompanyProfile();
      }

      setLoading(false);
      console.log("[CRM Dashboard] fetchData completed successfully");
    } catch (error) {
      console.error("[CRM Dashboard] Error fetching data:", error);
      if (error.response?.status === 401) {
        navigate("/");
      }
      setLoading(false);
    }
  };

  const fetchLeads = async (status = "all", assigned = "all") => {
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.append("status", status);
      if (assigned && assigned !== "all") params.append("assigned_to", assigned);

      const response = await axios.get(`${API}/leads?${params.toString()}`, { withCredentials: true });
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  const updateLead = async (leadId, data) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, data, { withCredentials: true });
      toast.success("Lead updated successfully");
      fetchLeads(filterStatus, filterAssigned);
      if (selectedLead?.id === leadId) {
        const updatedLead = await axios.get(`${API}/leads/${leadId}`, { withCredentials: true });
        setSelectedLead(updatedLead.data);
      }
      fetchData(); // Refresh analytics
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead");
    }
  };

  const deleteLead = async (leadId, leadName) => {
    console.log("[Delete Lead] Starting deletion for:", leadId, leadName);
    
    if (!window.confirm(`Are you sure you want to permanently delete lead "${leadName}"? This action cannot be undone and will remove all associated notes and reminders.`)) {
      console.log("[Delete Lead] User cancelled deletion");
      return;
    }

    console.log("[Delete Lead] User confirmed, sending DELETE request");

    try {
      const response = await axios.delete(`${API}/leads/${leadId}`, { withCredentials: true });
      console.log("[Delete Lead] DELETE response:", response.data);
      toast.success(`Lead "${leadName}" deleted successfully`);
      setLeadDialogOpen(false);
      setSelectedLead(null);
      await fetchLeads(filterStatus, filterAssigned);
      await fetchData(); // Refresh analytics
      console.log("[Delete Lead] Completed successfully");
    } catch (error) {
      console.error("[Delete Lead] Error:", error);
      console.error("[Delete Lead] Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to delete lead");
    }
  };


  const viewLeadDetails = async (lead) => {
    setSelectedLead(lead);
    try {
      const notesRes = await axios.get(`${API}/leads/${lead.id}/notes`, { withCredentials: true });
      setNotes(notesRes.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;

    try {
      await axios.post(`${API}/leads/${selectedLead.id}/notes`, {
        content: newNote
      }, { withCredentials: true });

      toast.success("Note added");
      setNewNote("");
      
      // Refresh notes
      const notesRes = await axios.get(`${API}/leads/${selectedLead.id}/notes`, { withCredentials: true });
      setNotes(notesRes.data);
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to add note");
    }
  };

  const addReminder = async () => {
    if (!newReminder.date || !newReminder.message) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(`${API}/leads/${selectedLead.id}/reminders`, {
        reminder_date: new Date(newReminder.date).toISOString(),
        message: newReminder.message
      }, { withCredentials: true });

      toast.success("Reminder added");
      setNewReminder({ date: "", message: "" });
      fetchData(); // Refresh reminders
    } catch (error) {
      console.error("Error adding reminder:", error);
      toast.error("Failed to add reminder");
    }
  };

  const completeReminder = async (reminderId) => {
    try {
      await axios.put(`${API}/reminders/${reminderId}`, {}, { withCredentials: true });
      toast.success("Reminder completed");
      fetchData();
    } catch (error) {
      console.error("Error completing reminder:", error);
      toast.error("Failed to complete reminder");
    }
  };


  const toggleAutoApprove = async () => {
    setUpdatingSettings(true);
    try {
      await axios.put(`${API}/settings?auto_approve=${!autoApprove}`, {}, { withCredentials: true });
      setAutoApprove(!autoApprove);
      toast.success(`Auto-approve ${!autoApprove ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setUpdatingSettings(false);
    }
  };


  const fetchCompanyProfile = async () => {
    try {
      const response = await axios.get(`${API}/company/profile`, { withCredentials: true });
      setCompanyProfile(response.data);
      setProfileData({
        company_name: response.data.company_name || "",
        contact_person: response.data.contact_person || "",
        phone: response.data.phone || "",
        address: response.data.address || "",
        website: response.data.website || "",
        description: response.data.description || "",
        management_types: response.data.management_types || ["Residential", "Condo", "Commercial"]
      });
    } catch (error) {
      console.error("Error fetching company profile:", error);
    }
  };

  const handleProfileManagementTypeToggle = (type) => {
    setProfileData(prev => {
      const types = [...prev.management_types];
      const index = types.indexOf(type);
      
      if (index > -1) {
        // Remove type if already selected (but keep at least one)
        if (types.length > 1) {
          types.splice(index, 1);
        } else {
          toast.error("You must select at least one management type");
          return prev;
        }
      } else {
        // Add type if not selected
        types.push(type);
      }
      
      return { ...prev, management_types: types };
    });
  };

  const updateProfile = async () => {
    setUpdatingProfile(true);
    try {
      // Convert management_types array to comma-separated string
      const params = {
        ...profileData,
        management_types: profileData.management_types.join(',')
      };
      
      await axios.put(
        `${API}/company/profile`,
        null,
        {
          params: params,
          withCredentials: true
        }
      );
      toast.success("Profile updated successfully");
      setEditingProfile(false);
      fetchCompanyProfile();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setUpdatingProfile(false);
    }
  };

  const changePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(
        `${API}/company/change-password`,
        null,
        {
          params: {
            current_password: passwordData.current_password,
            new_password: passwordData.new_password
          },
          withCredentials: true
        }
      );
      toast.success("Password changed successfully");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      console.error("Error changing password:", error);
      if (error.response?.status === 401) {
        toast.error("Current password is incorrect");
      } else {
        toast.error("Failed to change password");
      }
    } finally {
      setChangingPassword(false);
    }
  };


  const fetchAdminProfile = async () => {
    try {
      const response = await axios.get(`${API}/admin/profile`, { withCredentials: true });
      setAdminProfile(response.data);
      setAdminProfileData({
        name: response.data.name || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        website: response.data.website || ""
      });
    } catch (error) {
      console.error("Error fetching admin profile:", error);
    }
  };

  const updateAdminProfile = async () => {
    setUpdatingAdminProfile(true);
    try {
      await axios.put(
        `${API}/admin/profile`,
        null,
        {
          params: adminProfileData,
          withCredentials: true
        }
      );
      toast.success("Profile updated successfully");
      setEditingAdminProfile(false);
      fetchAdminProfile();
    } catch (error) {
      console.error("Error updating admin profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setUpdatingAdminProfile(false);
    }
  };

  const changeAdminPassword = async () => {
    if (adminPasswordData.new_password !== adminPasswordData.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }

    if (adminPasswordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setChangingAdminPassword(true);
    try {
      await axios.put(
        `${API}/admin/change-password`,
        null,
        {
          params: {
            current_password: adminPasswordData.current_password,
            new_password: adminPasswordData.new_password
          },
          withCredentials: true
        }
      );
      toast.success("Password changed successfully");
      setAdminPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      console.error("Error changing admin password:", error);
      if (error.response?.status === 401) {
        toast.error("Current password is incorrect");
      } else {
        toast.error("Failed to change password");
      }
    } finally {
      setChangingAdminPassword(false);
    }
  };




  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700 border-blue-200",
      contacted: "bg-yellow-100 text-yellow-700 border-yellow-200",
      qualified: "bg-purple-100 text-purple-700 border-purple-200",
      converted: "bg-green-100 text-green-700 border-green-200",
      lost: "bg-blue-100 text-blue-700 border-blue-200"
    };
    return colors[status] || colors.new;
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "text-green-600";
    if (score >= 50) return "text-yellow-600";
    return "text-blue-600";
  };

  const handleFilterChange = (type, value) => {
    if (type === "status") {
      setFilterStatus(value);
      fetchLeads(value, filterAssigned);
    } else {
      setFilterAssigned(value);
      fetchLeads(filterStatus, value);
    }
  };

  if (loading || user === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="border-b border-slate-700 sticky top-0 z-50 shadow-lg" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Building2 className="h-8 w-8" style={{ color: '#3b82f6' }} />
              <span className="text-2xl font-bold text-white">Top Prop CRM</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
              )}
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-slate-300">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="border-slate-600 hover:bg-slate-700 text-white hover:text-white"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8" data-testid="crm-dashboard">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Leads</p>
                    <p className="text-3xl font-bold text-slate-900" data-testid="total-leads">{analytics.total_leads}</p>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">New Leads</p>
                    <p className="text-3xl font-bold text-slate-900" data-testid="new-leads">{analytics.new_leads}</p>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <UserPlus className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Converted</p>
                    <p className="text-3xl font-bold text-slate-900">{analytics.converted_leads}</p>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Qualified</p>
                    <p className="text-3xl font-bold text-slate-900">{analytics.qualified_leads}</p>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Star className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reminders Section */}
        {reminders.length > 0 && (
          <Card className="bg-amber-50 border-amber-200 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-amber-600" />
                Upcoming Reminders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {reminders.slice(0, 3).map((reminder) => (
                  <div key={reminder.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-200">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{reminder.message}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(reminder.reminder_date).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => completeReminder(reminder.id)}
                      data-testid={`complete-reminder-${reminder.id}`}
                    >
                      Complete
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content with Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`grid w-full max-w-md ${user?.role === "property_manager" ? "grid-cols-2" : "grid-cols-3"} bg-white border border-slate-200`}>
            <TabsTrigger value="leads" data-testid="leads-tab">Leads</TabsTrigger>
            {user?.role === "admin" && (
              <>
                <TabsTrigger value="companies" data-testid="companies-tab">Companies</TabsTrigger>
                <TabsTrigger value="settings" data-testid="settings-tab">Settings</TabsTrigger>
              </>
            )}
            {user?.role === "property_manager" && (
              <TabsTrigger value="settings" data-testid="settings-tab">Settings</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="leads">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Leads Management</CardTitle>
                    <CardDescription>View and manage all your property management leads</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-[180px]">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterLeadType} onValueChange={setFilterLeadType}>
                      <SelectTrigger className="w-[180px]">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="standard">Standard Leads</SelectItem>
                        <SelectItem value="rent_worth">💰 Rent Worth Leads</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {leads.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No leads found</p>
                </div>
              ) : (
                leads.filter(lead => {
                  // Filter by lead type
                  if (filterLeadType === "standard" && lead.lead_type === "rent_worth") return false;
                  if (filterLeadType === "rent_worth" && lead.lead_type !== "rent_worth") return false;
                  return true;
                }).map((lead) => (
                  <Dialog 
                    key={lead.id} 
                    open={leadDialogOpen && selectedLead?.id === lead.id}
                    onOpenChange={(open) => {
                      setLeadDialogOpen(open);
                      if (open) {
                        viewLeadDetails(lead);
                      } else {
                        setSelectedLead(null);
                      }
                    }}
                  >
                    <DialogTrigger asChild>
                      <div
                        className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer bg-white"
                        data-testid={`lead-${lead.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-slate-900">{lead.name}</h3>
                              <Badge className={getStatusColor(lead.status)} variant="outline">
                                {lead.status}
                              </Badge>
                              {lead.score > 0 && (
                                <div className="flex items-center gap-1">
                                  <Star className={`h-4 w-4 ${getScoreColor(lead.score)}`} />
                                  <span className={`text-sm font-medium ${getScoreColor(lead.score)}`}>
                                    {lead.score}
                                  </span>
                                </div>
                              )}
                              {/* Engagement indicators */}
                              {lead.companies_clicked && lead.companies_clicked.length > 0 && (
                                <Badge className="bg-blue-100 text-blue-800 border-blue-300" variant="outline">
                                  🔥 Hot
                                </Badge>
                              )}
                              {lead.viewed_matches_at && (!lead.companies_clicked || lead.companies_clicked.length === 0) && (
                                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300" variant="outline">
                                  👁️ Viewed
                                </Badge>
                              )}
                              {/* Rent Worth Lead Indicator */}
                              {lead.lead_type === "rent_worth" && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-semibold" variant="outline">
                                  💰 Rent Worth?®
                                </Badge>
                              )}
                              {/* Inline Delete Button - Admin Only */}
                              {user?.role === "admin" && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteLead(lead.id, lead.name);
                                  }}
                                  className="ml-auto px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors border border-blue-200"
                                  title="Delete lead"
                                >
                                  🗑️ Delete
                                </button>
                              )}
                            </div>
                            <div className="grid md:grid-cols-3 gap-3 text-sm text-slate-600">
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                {lead.email}
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                {lead.phone}
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {lead.property_address}
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                              <span>{lead.property_type} • {lead.num_units} units</span>
                              <span>•</span>
                              <span>{lead.management_status}</span>
                              {lead.created_at && (
                                <>
                                  <span>•</span>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    <span>{new Date(lead.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                  </div>
                                </>
                              )}
                              {lead.assigned_to && (
                                <>
                                  <span>•</span>
                                  <span>Assigned to: {users.find(u => u.id === lead.assigned_to)?.name || 'Unknown'}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </DialogTrigger>

                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Lead Details - {selectedLead?.name}</DialogTitle>
                      </DialogHeader>

                      {selectedLead && (
                        <Tabs defaultValue="details" className="w-full">
                          <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="details">Details</TabsTrigger>
                            <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
                            <TabsTrigger value="reminders">Reminders</TabsTrigger>
                          </TabsList>

                          <TabsContent value="details" className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <Label>Status</Label>
                                <Select
                                  value={selectedLead.status}
                                  onValueChange={(value) => updateLead(selectedLead.id, { status: value })}
                                >
                                  <SelectTrigger data-testid="update-status">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="new">New</SelectItem>
                                    <SelectItem value="contacted">Contacted</SelectItem>
                                    <SelectItem value="qualified">Qualified</SelectItem>
                                    <SelectItem value="converted">Converted</SelectItem>
                                    <SelectItem value="lost">Lost</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <Label>Lead Score (0-100)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={selectedLead.score}
                                  onChange={(e) => updateLead(selectedLead.id, { score: parseInt(e.target.value) })}
                                  data-testid="update-score"
                                />
                              </div>

                              <div className="space-y-2 md:col-span-2">
                                <Label>Assign To</Label>
                                <Select
                                  value={selectedLead.assigned_to || "unassigned"}
                                  onValueChange={(value) => updateLead(selectedLead.id, { assigned_to: value === "unassigned" ? null : value })}
                                >
                                  <SelectTrigger data-testid="assign-to">
                                    <SelectValue placeholder="Unassigned" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unassigned">Unassigned</SelectItem>
                                    {users.map((u) => (
                                      <SelectItem key={u.id} value={u.id}>
                                        {u.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="border-t pt-4 space-y-3">
                              <h4 className="font-semibold text-slate-900">Contact Information</h4>
                              <div className="grid md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-slate-500">Email</p>
                                  <p className="text-slate-900">{selectedLead.email}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Phone</p>
                                  <p className="text-slate-900">{selectedLead.phone}</p>
                                </div>
                                <div className="md:col-span-2">
                                  <p className="text-slate-500">Property Address</p>
                                  <p className="text-slate-900">{selectedLead.property_address}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Property Type</p>
                                  <p className="text-slate-900">{selectedLead.property_type}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Number of Units</p>
                                  <p className="text-slate-900">{selectedLead.num_units}</p>
                                </div>
                                <div className="md:col-span-2">
                                  <p className="text-slate-500">Management Status</p>
                                  <p className="text-slate-900">{selectedLead.management_status}</p>
                                </div>
                              </div>
                            </div>

                            {/* Lead Engagement Tracking Section */}
                            <div className="border-t pt-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                                <span>📊</span> Lead Engagement Tracking
                              </h4>
                              <div className="grid md:grid-cols-2 gap-4 text-sm">
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                  <p className="text-blue-700 font-semibold mb-1 flex items-center gap-2">
                                    <span>👁️</span> Viewed Matches
                                  </p>
                                  {selectedLead.viewed_matches_at ? (
                                    <div>
                                      <p className="text-blue-900 font-medium">
                                        ✅ Yes
                                      </p>
                                      <p className="text-xs text-blue-600 mt-1">
                                        {new Date(selectedLead.viewed_matches_at).toLocaleString()}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-blue-700">❌ Not yet</p>
                                  )}
                                </div>

                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                  <p className="text-green-700 font-semibold mb-1 flex items-center gap-2">
                                    <span>🎯</span> Companies Clicked
                                  </p>
                                  {selectedLead.companies_clicked && selectedLead.companies_clicked.length > 0 ? (
                                    <div>
                                      <p className="text-green-900 font-medium">
                                        ✅ {selectedLead.companies_clicked.length} {selectedLead.companies_clicked.length === 1 ? 'company' : 'companies'}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-green-700">❌ None yet</p>
                                  )}
                                </div>
                              </div>

                              {selectedLead.viewed_matches_at && (!selectedLead.companies_clicked || selectedLead.companies_clicked.length === 0) && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                                  <p className="text-yellow-800 flex items-start gap-2">
                                    <span className="text-lg">⚠️</span>
                                    <span>
                                      <strong>Warm Lead!</strong> This lead viewed their matches but hasn't clicked any companies yet. 
                                      Consider reaching out proactively.
                                    </span>
                                  </p>
                                </div>
                              )}

                              {selectedLead.companies_clicked && selectedLead.companies_clicked.length > 0 && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                                  <p className="text-blue-800 flex items-start gap-2">
                                    <span className="text-lg">🔥</span>
                                    <span>
                                      <strong>Hot Lead!</strong> This lead is actively engaged and comparing companies. 
                                      High conversion potential!
                                    </span>
                                  </p>
                                </div>
                              )}
                            </div>
                          </TabsContent>

                          <TabsContent value="notes" className="space-y-4">
                            <div className="space-y-3">
                              <Label>Add Note</Label>
                              <Textarea
                                placeholder="Add a note..."
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                data-testid="note-input"
                              />
                              <Button onClick={addNote} data-testid="add-note-button">
                                <MessageSquare className="h-4 w-4 mr-2" />
                                Add Note
                              </Button>
                            </div>

                            <div className="border-t pt-4 space-y-3">
                              <h4 className="font-semibold text-slate-900">Previous Notes</h4>
                              {notes.length === 0 ? (
                                <p className="text-sm text-slate-500">No notes yet</p>
                              ) : (
                                <div className="space-y-3">
                                  {notes.map((note) => (
                                    <div key={note.id} className="bg-slate-50 p-3 rounded-lg" data-testid={`note-${note.id}`}>
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-slate-900">{note.user_name}</span>
                                        <span className="text-xs text-slate-500">
                                          {new Date(note.created_at).toLocaleString()}
                                        </span>
                                      </div>
                                      <p className="text-sm text-slate-700">{note.content}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </TabsContent>

                          <TabsContent value="reminders" className="space-y-4">
                            <div className="space-y-3">
                              <Label>Add Reminder</Label>
                              <div className="grid gap-3">
                                <Input
                                  type="datetime-local"
                                  value={newReminder.date}
                                  onChange={(e) => setNewReminder({ ...newReminder, date: e.target.value })}
                                  data-testid="reminder-date-input"
                                />
                                <Input
                                  placeholder="Reminder message"
                                  value={newReminder.message}
                                  onChange={(e) => setNewReminder({ ...newReminder, message: e.target.value })}
                                  data-testid="reminder-message-input"
                                />
                                <Button onClick={addReminder} data-testid="add-reminder-button">
                                  <Bell className="h-4 w-4 mr-2" />
                                  Add Reminder
                                </Button>
                              </div>
                            </div>
                          </TabsContent>
                        </Tabs>
                      )}
                      
                      {/* Delete Lead Button - Only for Admin */}
                      {selectedLead && user?.role === "admin" && (
                        <div className="border-t pt-4 mt-4">
                          <Button
                            onClick={() => deleteLead(selectedLead.id, selectedLead.name)}
                            variant="outline"
                            className="w-full border-blue-400 text-blue-700 hover:bg-blue-50 hover:border-blue-500"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Delete Lead Permanently
                          </Button>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                ))
              )}
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="companies">
            {user?.role === "admin" ? (
              <CompaniesManagement />
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6">
                  <p className="text-slate-600">You don't have permission to view this section.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>


          <TabsContent value="settings">
            {user?.role === "admin" ? (
              <div className="space-y-6">
                {/* System Settings Card */}
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle>System Settings</CardTitle>
                    <CardDescription>Configure system settings and automation</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div className="space-y-1">
                        <h3 className="font-semibold text-slate-900">Auto-Approve Companies</h3>
                        <p className="text-sm text-slate-500">Automatically approve and activate new company signups</p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className={`h-2 w-2 rounded-full ${autoApprove ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          <span className="text-xs font-medium text-slate-600">
                            {autoApprove ? 'Currently Enabled' : 'Currently Disabled'}
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={toggleAutoApprove}
                        disabled={updatingSettings}
                        variant={autoApprove ? "destructive" : "default"}
                        className={autoApprove ? "" : "bg-green-600 hover:bg-green-700"}
                      >
                        {updatingSettings ? "Updating..." : autoApprove ? "Disable" : "Enable"}
                      </Button>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">What does this do?</h4>
                      <ul className="space-y-2 text-sm text-blue-800">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600">•</span>
                          <span><strong>Enabled:</strong> New companies can login immediately after signup</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600">•</span>
                          <span><strong>Disabled:</strong> You must manually approve companies in the Companies tab</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600">•</span>
                          <span>Perfect for testing when <strong>Enabled</strong>, use <strong>Disabled</strong> in production</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Admin Profile Section */}
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle>Admin Profile</CardTitle>
                    <CardDescription>Update your admin information</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!editingAdminProfile ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-slate-500">Name</p>
                            <p className="text-base text-slate-900">{adminProfile?.name}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-500">Email</p>
                            <p className="text-base text-slate-900">{adminProfile?.email}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-500">Phone</p>
                            <p className="text-base text-slate-900">{adminProfile?.phone || 'Not set'}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-500">Website</p>
                            <p className="text-base text-slate-900">{adminProfile?.website || 'Not set'}</p>
                          </div>
                        </div>
                        <Button onClick={() => setEditingAdminProfile(true)}>Edit Profile</Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                              value={adminProfileData.name}
                              onChange={(e) => setAdminProfileData({...adminProfileData, name: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                              value={adminProfileData.email}
                              onChange={(e) => setAdminProfileData({...adminProfileData, email: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                              value={adminProfileData.phone}
                              onChange={(e) => setAdminProfileData({...adminProfileData, phone: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Website</Label>
                            <Input
                              value={adminProfileData.website}
                              onChange={(e) => setAdminProfileData({...adminProfileData, website: e.target.value})}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={updateAdminProfile} disabled={updatingAdminProfile}>
                            {updatingAdminProfile ? "Saving..." : "Save Changes"}
                          </Button>
                          <Button variant="outline" onClick={() => setEditingAdminProfile(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Change Admin Password Section */}
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle>Change Password</CardTitle>
                    <CardDescription>Update your login password</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        value={adminPasswordData.current_password}
                        onChange={(e) => setAdminPasswordData({...adminPasswordData, current_password: e.target.value})}
                        placeholder="Enter current password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <Input
                        type="password"
                        value={adminPasswordData.new_password}
                        onChange={(e) => setAdminPasswordData({...adminPasswordData, new_password: e.target.value})}
                        placeholder="Enter new password (min 8 characters)"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={adminPasswordData.confirm_password}
                        onChange={(e) => setAdminPasswordData({...adminPasswordData, confirm_password: e.target.value})}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <Button onClick={changeAdminPassword} disabled={changingAdminPassword}>
                      {changingAdminPassword ? "Changing..." : "Change Password"}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ) : user?.role === "property_manager" ? (
              <div className="space-y-6">
                {/* Company Profile Section */}
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle>Company Profile</CardTitle>
                    <CardDescription>Update your company information</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!editingProfile ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-slate-500">Company Name</p>
                            <p className="text-base text-slate-900">{companyProfile?.company_name}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-500">Contact Person</p>
                            <p className="text-base text-slate-900">{companyProfile?.contact_person}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-500">Email</p>
                            <p className="text-base text-slate-900">{companyProfile?.email}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-500">Phone</p>
                            <p className="text-base text-slate-900">{companyProfile?.phone}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-sm font-medium text-slate-500">Website</p>
                            <p className="text-base text-slate-900">{companyProfile?.website}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-sm font-medium text-slate-500">Address</p>
                            <p className="text-base text-slate-900">{companyProfile?.address}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-sm font-medium text-slate-500">Management Types</p>
                            <p className="text-base text-slate-900">{companyProfile?.management_types?.join(', ') || 'Not set'}</p>
                          </div>
                        </div>
                        <Button onClick={() => setEditingProfile(true)}>Edit Profile</Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Company Name</Label>
                            <Input
                              value={profileData.company_name}
                              onChange={(e) => setProfileData({...profileData, company_name: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Contact Person</Label>
                            <Input
                              value={profileData.contact_person}
                              onChange={(e) => setProfileData({...profileData, contact_person: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                              value={profileData.phone}
                              onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Website</Label>
                            <Input
                              value={profileData.website}
                              onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                            />
                          </div>
                          <div className="col-span-2 space-y-2">
                            <Label>Address</Label>
                            <Input
                              value={profileData.address}
                              onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                            />
                          </div>
                          <div className="col-span-2 space-y-2">
                            <Label>Description</Label>
                            <textarea
                              className="w-full p-2 border border-slate-300 rounded-md"
                              rows="3"
                              value={profileData.description}
                              onChange={(e) => setProfileData({...profileData, description: e.target.value})}
                            />
                          </div>
                          <div className="col-span-2 space-y-2">
                            <Label>Management Types (Select all that apply)</Label>
                            <div className="space-y-3 p-4 border border-slate-200 rounded-lg bg-slate-50">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  id="profile-type-residential"
                                  checked={profileData.management_types.includes("Residential")}
                                  onChange={() => handleProfileManagementTypeToggle("Residential")}
                                  className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="profile-type-residential" className="text-sm font-medium text-slate-900 cursor-pointer">
                                  Residential Property Management
                                </label>
                              </div>
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  id="profile-type-condo"
                                  checked={profileData.management_types.includes("Condo")}
                                  onChange={() => handleProfileManagementTypeToggle("Condo")}
                                  className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="profile-type-condo" className="text-sm font-medium text-slate-900 cursor-pointer">
                                  Condo / Strata Management
                                </label>
                              </div>
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  id="profile-type-commercial"
                                  checked={profileData.management_types.includes("Commercial")}
                                  onChange={() => handleProfileManagementTypeToggle("Commercial")}
                                  className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="profile-type-commercial" className="text-sm font-medium text-slate-900 cursor-pointer">
                                  Commercial Property Management
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={updateProfile} disabled={updatingProfile}>
                            {updatingProfile ? "Saving..." : "Save Changes"}
                          </Button>
                          <Button variant="outline" onClick={() => setEditingProfile(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Change Password Section */}
                <Card className="bg-white border-slate-200">
                  <CardHeader>
                    <CardTitle>Change Password</CardTitle>
                    <CardDescription>Update your login password</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                        placeholder="Enter current password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <Input
                        type="password"
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                        placeholder="Enter new password (min 8 characters)"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <Button onClick={changePassword} disabled={changingPassword}>
                      {changingPassword ? "Changing..." : "Change Password"}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6">
                  <p className="text-slate-600">You don't have permission to view this section.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}